/**
 * Role detection with confidence scores (brief DET-02, DET-03).
 *
 * Each variable is scored 0..1 for each role from statistical
 * signatures, name patterns, label keywords, value-label meaning,
 * and preset hints. Signals add points and leave a plain-language
 * reason; brief disqualifiers cap the score. Combination is a
 * capped sum — the weights are heuristics, documented here, and
 * every score can be explained from its reasons.
 *
 * Two phases (DET-01 anticipates this): provisional singleton
 * winners (area, stratum) are picked from name/statistical signals
 * first, then cross-variable signals that need them (within-area
 * constancy, PSU nesting, name one-to-one) are scored, and the
 * table is re-profiled with within-area statistics.
 *
 * Assignment follows DET-03: >= 0.85 accepted, 0.50-0.85 assigned
 * for review, below 0.50 unassigned. Singleton roles take exactly
 * one column; when two eligible columns score within 0.10 for a
 * singleton, the assignment is marked contested — the user decides.
 */
import { columnIndex } from '@sae/io';
import { profileTable } from './profile.js';
import { GENERIC_PRESET } from './presets.js';
export const ACCEPT_THRESHOLD = 0.85;
export const REVIEW_THRESHOLD = 0.5;
export const CONTEST_WINDOW = 0.1;
const MICRO_SINGLETONS = [
    'area-id',
    'weight',
    'stratum',
    'psu',
    'psu-population-count',
    'area-name',
    'respondent-id',
];
const AREA_SINGLETONS = [
    'area-id',
    'estimate',
    'variance',
    'standard-error',
    'sample-size',
    'population-count',
    'area-name',
];
const MICRO_MULTI = ['indicator', 'subgroup', 'covariate'];
const AREA_MULTI = ['covariate'];
function combine(signals, caps) {
    let score = 0;
    const reasons = [];
    for (const s of signals) {
        score += s.points;
        reasons.push(s.reason);
    }
    for (const c of caps) {
        if (score > c.cap) {
            score = c.cap;
            reasons.push(c.reason);
        }
    }
    return { score: Math.min(1, Math.max(0, score)), reasons };
}
function normName(name) {
    return name.toLowerCase().replace(/[^a-z0-9]/g, '');
}
function nameHas(col, re) {
    return re.test(normName(col.name)) || re.test((col.label ?? '').toLowerCase());
}
function presetSignals(col, role, ctx) {
    const out = [];
    for (const hint of ctx.preset.hints) {
        if (hint.role !== role)
            continue;
        const nameHit = hint.names?.some((n) => n.toLowerCase() === col.name.toLowerCase()) ??
            false;
        const patternHit = hint.pattern?.test(col.name) ?? false;
        if (nameHit || patternHit)
            out.push({ points: hint.boost, reason: hint.reason });
    }
    return out;
}
/** True when each value of `inner` co-occurs with exactly one value of `outer`. */
function nestsWithin(table, innerName, outerName) {
    const ii = columnIndex(table, innerName);
    const oi = columnIndex(table, outerName);
    if (ii < 0 || oi < 0)
        return false;
    const seen = new Map();
    for (const row of table.rows) {
        const inner = row[ii];
        const outer = row[oi];
        if (inner === null || outer === null)
            continue;
        const prev = seen.get(inner);
        if (prev === undefined)
            seen.set(inner, outer);
        else if (prev !== outer)
            return false;
    }
    return seen.size > 0;
}
/** True when columns a and b are in one-to-one correspondence. */
function oneToOne(table, aName, bName) {
    return (nestsWithin(table, aName, bName) && nestsWithin(table, bName, aName));
}
/**
 * True when `inner` takes at most one non-missing value inside
 * every level of `outer` (rows missing either value are ignored).
 * The shape of a per-stratum frame value carried on respondent
 * rows: the frame PSU count of a stratum is one number, repeated
 * on that stratum's rows.
 */
function constantWithin(table, innerName, outerName) {
    const ii = columnIndex(table, innerName);
    const oi = columnIndex(table, outerName);
    if (ii < 0 || oi < 0)
        return false;
    const seen = new Map();
    let rows = 0;
    for (const row of table.rows) {
        const inner = row[ii];
        const outer = row[oi];
        if (inner === null || outer === null)
            continue;
        rows += 1;
        const prev = seen.get(outer);
        if (prev === undefined)
            seen.set(outer, inner);
        else if (prev !== inner)
            return false;
    }
    return rows > 0;
}
const YES_LABEL_RE = /^\s*(yes|y|true|1)\b/i;
const NO_LABEL_RE = /^\s*(no|n|false|0)\b/i;
/**
 * Distinct values that are not label-declared missing. The count is
 * exact (distinctCount less the suspected-missing values, which the
 * profile enumerates completely); the value list is exact whenever
 * the column has at most five distinct values, the only case the
 * {0,1} / {1,2} set checks below rely on.
 */
function substantiveInfo(col) {
    const suspected = new Set(col.suspectedMissing.map((v) => v.value));
    const values = col.distinctCount <= 5
        ? col.topValues.map((v) => v.value).filter((v) => !suspected.has(v))
        : [];
    return { count: col.distinctCount - suspected.size, values };
}
function scoreMicrodata(col, role, ctx) {
    const s = [...presetSignals(col, role, ctx)];
    const caps = [];
    const num = col.numeric;
    switch (role) {
        case 'area-id': {
            if (nameHas(col, /area|county|district|division|region|lea|fips|site|geo|school/))
                s.push({ points: 0.45, reason: 'name or label suggests an area identifier' });
            if (col.distinctCount >= 2 && col.uniqueness < 1)
                s.push({ points: 0.2, reason: `${col.distinctCount} distinct values repeat across rows` });
            if (col.missingShare < 0.05)
                s.push({ points: 0.1, reason: 'almost never missing' });
            if (col.type === 'string' || (num?.integerOnly ?? false))
                s.push({ points: 0.1, reason: 'values are codes (text or whole numbers)' });
            if (col.uniqueness >= 1)
                caps.push({ cap: 0.1, reason: 'unique per row: an identifier, not an area key (brief 7.2)' });
            if (num !== null && !num.integerOnly)
                caps.push({ cap: 0.2, reason: 'continuous values cannot be area codes' });
            if (col.distinctCount < 2)
                caps.push({ cap: 0.05, reason: 'fewer than two distinct values' });
            break;
        }
        case 'area-name': {
            if (nameHas(col, /name/))
                s.push({ points: 0.45, reason: 'name or label mentions a name' });
            if (col.type === 'string')
                s.push({ points: 0.15, reason: 'text values' });
            if (ctx.provisionalArea !== undefined &&
                col.name !== ctx.provisionalArea &&
                oneToOne(ctx.table, col.name, ctx.provisionalArea))
                s.push({ points: 0.35, reason: 'one-to-one with the area identifier' });
            else if (ctx.provisionalArea !== undefined)
                caps.push({ cap: 0.4, reason: 'not one-to-one with the area identifier' });
            break;
        }
        case 'weight': {
            if (nameHas(col, /weight|wt|wgt|finalwt/))
                s.push({ points: 0.45, reason: 'name or label suggests a weight' });
            if (col.type === 'number')
                s.push({ points: 0.1, reason: 'numeric' });
            if (num !== null && num.min > 0)
                s.push({ points: 0.15, reason: 'all values positive' });
            if (col.missingShare === 0)
                s.push({ points: 0.1, reason: 'no missing values' });
            if (num !== null && !num.integerOnly)
                s.push({ points: 0.1, reason: 'continuous values, as weights usually are' });
            if (num !== null && num.mean > 1)
                s.push({ points: 0.05, reason: 'mean above 1, as population weights usually are' });
            if (num !== null && num.min <= 0)
                caps.push({ cap: 0.15, reason: 'zeros or negatives present: weights must be positive (brief 7.2)' });
            if (col.valueLabelCoverage !== null && col.valueLabelCoverage > 0)
                caps.push({ cap: 0.3, reason: 'values carry category labels: a coded variable, not a weight' });
            if (col.missingShare > 0)
                caps.push({ cap: 0.45, reason: 'missing values present: an analysis weight is required for every respondent' });
            break;
        }
        case 'stratum': {
            if (nameHas(col, /stratum|strata|strat/))
                s.push({ points: 0.5, reason: 'name or label suggests a stratum' });
            if (num?.integerOnly)
                s.push({ points: 0.1, reason: 'whole-number codes' });
            if (col.distinctCount >= 2 && col.distinctCount <= 1000)
                s.push({ points: 0.15, reason: `${col.distinctCount} levels, in the stratum range` });
            if (col.uniqueness >= 1)
                caps.push({ cap: 0.1, reason: 'unique per row: not a stratum (brief 7.2)' });
            break;
        }
        case 'psu': {
            if (nameHas(col, /psu|cluster|school/))
                s.push({ points: 0.5, reason: 'name or label suggests a PSU or cluster' });
            if (col.distinctCount >= 2 && col.uniqueness < 1)
                s.push({ points: 0.15, reason: 'values repeat across rows, as clusters do' });
            if (ctx.provisionalStratum !== undefined &&
                col.name !== ctx.provisionalStratum &&
                nestsWithin(ctx.table, col.name, ctx.provisionalStratum))
                s.push({ points: 0.25, reason: 'each value sits inside exactly one stratum (nested)' });
            if (col.uniqueness >= 1)
                caps.push({ cap: 0.2, reason: 'unique per row: not a cluster variable in a clustered survey (brief 7.2)' });
            break;
        }
        case 'psu-population-count': {
            // The design frame's PSU total for each row's stratum
            // (Addendum 37): one positive whole number per stratum,
            // constant within the stratum. Auto-assignment requires a
            // PSU reference in the name or label — shape alone (a
            // constant-within-stratum count) is capped below the review
            // band, because many per-stratum constants are not frame
            // counts; the user can still assign the role by hand.
            const psuWord = /psu|cluster/.test(normName(col.name)) ||
                /psu|cluster/.test((col.label ?? '').toLowerCase());
            const frameWord = /pop|frame|total|universe|count/.test(normName(col.name)) ||
                /populat|frame|total|universe|count/.test((col.label ?? '').toLowerCase());
            if (psuWord && frameWord)
                s.push({ points: 0.5, reason: 'name or label suggests the frame PSU count for a stratum' });
            else if (psuWord)
                s.push({ points: 0.25, reason: 'name or label mentions PSUs' });
            if (col.type === 'number')
                s.push({ points: 0.05, reason: 'numeric' });
            if (num?.integerOnly)
                s.push({ points: 0.1, reason: 'whole-number counts' });
            if (num !== null && num.min > 0)
                s.push({ points: 0.05, reason: 'all values positive' });
            if (ctx.provisionalStratum !== undefined &&
                col.name !== ctx.provisionalStratum &&
                constantWithin(ctx.table, col.name, ctx.provisionalStratum))
                s.push({ points: 0.45, reason: 'constant within every stratum: one frame value per stratum' });
            if (col.uniqueness >= 1)
                caps.push({ cap: 0.1, reason: 'unique per row: not a per-stratum count (brief 7.2)' });
            if (col.valueLabelCoverage !== null && col.valueLabelCoverage > 0)
                caps.push({ cap: 0.3, reason: 'values carry category labels: a coded variable, not a count' });
            if (!psuWord)
                caps.push({
                    cap: 0.45,
                    reason: 'no PSU reference in the name or label: a frame count is not assigned on shape alone',
                });
            break;
        }
        case 'respondent-id': {
            if (nameHas(col, /respondent|caseid|serial|^id$|personid/))
                s.push({ points: 0.3, reason: 'name or label suggests a respondent identifier' });
            if (col.uniqueness >= 1 && col.missingShare === 0)
                s.push({ points: 0.45, reason: 'unique per row with no missing values' });
            if (col.uniqueness < 1)
                caps.push({ cap: 0.2, reason: 'values repeat: not a respondent identifier' });
            break;
        }
        case 'indicator': {
            const substantive = substantiveInfo(col);
            const labelText = (col.label ?? '').toLowerCase();
            if (substantive.count === 2) {
                s.push({ points: 0.35, reason: 'exactly two substantive values' });
                const set = new Set(substantive.values.map(String));
                if (set.has('0') && set.has('1'))
                    s.push({ points: 0.2, reason: 'values are 0 and 1' });
                if (set.has('1') && set.has('2'))
                    s.push({ points: 0.15, reason: 'values are 1 and 2, the common yes/no coding' });
            }
            const hasYesNo = col.topValues.some((v) => v.label !== undefined && YES_LABEL_RE.test(v.label)) && col.topValues.some((v) => v.label !== undefined && NO_LABEL_RE.test(v.label));
            if (hasYesNo)
                s.push({ points: 0.25, reason: 'value labels read as yes and no' });
            if (/^(q|qn)\d/i.test(col.name) || labelText.includes('?'))
                s.push({ points: 0.05, reason: 'looks like a survey question variable' });
            if (substantive.count > 2)
                caps.push({
                    cap: 0.3,
                    reason: 'more than two substantive values: an indicator needs an explicit rule to dichotomize (brief 7.2)',
                });
            if (substantive.count < 2)
                caps.push({ cap: 0.1, reason: 'fewer than two substantive values' });
            break;
        }
        case 'subgroup': {
            if (col.distinctCount >= 2 && col.distinctCount <= 30)
                s.push({ points: 0.3, reason: `${col.distinctCount} categories, in the subgroup range` });
            const demographic = ctx.preset.demographicNames.some((d) => normName(col.name) === d || normName(col.name).includes(d));
            if (demographic || nameHas(col, /sex|gender|grade|age|race|ethnic|identity|orientation|language|iep|disab/))
                s.push({ points: 0.35, reason: 'name or label suggests a demographic grouping' });
            if (col.valueLabelCoverage !== null && col.valueLabelCoverage >= 0.8)
                s.push({ points: 0.15, reason: 'categories are labeled' });
            if (col.type === 'string' && col.distinctCount >= 2 && col.distinctCount <= 50)
                s.push({ points: 0.15, reason: 'text categories' });
            if (col.withinArea !== null && !col.withinArea.constantWithinAreas)
                s.push({ points: 0.1, reason: 'varies within areas, as a respondent grouping does' });
            if (col.uniqueness >= 1)
                caps.push({ cap: 0.1, reason: 'unique per row: not a grouping' });
            if (num !== null && !num.integerOnly && col.distinctCount > 30)
                caps.push({ cap: 0.15, reason: 'continuous values: not a category set (brief 7.2)' });
            break;
        }
        case 'covariate': {
            if (col.type === 'number')
                s.push({ points: 0.15, reason: 'numeric' });
            if (col.withinArea?.constantWithinAreas)
                s.push({ points: 0.45, reason: 'constant within every area: an area-level value carried on respondent rows' });
            if ((col.withinArea?.betweenAreaVariance ?? 0) > 0)
                s.push({ points: 0.15, reason: 'varies across areas' });
            if (nameHas(col, /pct|percent|rate|poverty|income|enroll|locale|median/))
                s.push({ points: 0.2, reason: 'name or label suggests an area characteristic' });
            if (col.withinArea !== null && !col.withinArea.constantWithinAreas)
                caps.push({ cap: 0.2, reason: 'varies within areas: not an area-level covariate (QC-14)' });
            if (col.distinctCount < 2)
                caps.push({ cap: 0.1, reason: 'constant overall: no information (brief 7.2)' });
            break;
        }
        default:
            break;
    }
    return combine(s, caps);
}
function scoreAreaLevel(col, role, ctx) {
    const s = [...presetSignals(col, role, ctx)];
    const caps = [];
    const num = col.numeric;
    switch (role) {
        case 'area-id': {
            // Area-level variant: one row per area, so the identifier is
            // unique per row by construction (the microdata disqualifier
            // of brief 7.2 does not apply here).
            if (nameHas(col, /area|county|district|division|region|lea|fips|site|geo/))
                s.push({ points: 0.45, reason: 'name or label suggests an area identifier' });
            if (col.distinctCount >= 2)
                s.push({ points: 0.2, reason: `${col.distinctCount} distinct values, one per row` });
            if (col.missingShare < 0.05)
                s.push({ points: 0.1, reason: 'almost never missing' });
            if (col.type === 'string' || (num?.integerOnly ?? false))
                s.push({ points: 0.1, reason: 'values are codes (text or whole numbers)' });
            if (col.distinctCount < 2)
                caps.push({ cap: 0.05, reason: 'fewer than two distinct values' });
            if (num !== null && !num.integerOnly)
                caps.push({ cap: 0.2, reason: 'continuous values cannot be area codes' });
            break;
        }
        case 'area-name':
            return scoreMicrodata(col, 'area-name', ctx);
        case 'estimate': {
            if (nameHas(col, /est|pct|percent|proportion|direct|rate/))
                s.push({ points: 0.35, reason: 'name or label suggests a direct estimate' });
            if (num !== null && num.min >= 0 && num.max <= 1.0001)
                s.push({ points: 0.3, reason: 'all values in the proportion range 0 to 1' });
            else if (num !== null && num.min >= 0 && num.max <= 100.01)
                s.push({ points: 0.2, reason: 'all values in the percent range 0 to 100' });
            if (num !== null && (num.max > 100.01 || num.min < 0))
                caps.push({ cap: 0.15, reason: 'values outside any proportion or percent range' });
            break;
        }
        case 'variance': {
            if (nameHas(col, /variance|^var/))
                s.push({ points: 0.5, reason: 'name or label says variance' });
            if (num !== null && num.min >= 0 && num.max < 0.25)
                s.push({ points: 0.25, reason: 'small non-negative values, as sampling variances of proportions are' });
            if (num !== null && num.min < 0)
                caps.push({ cap: 0.05, reason: 'negative values: a variance cannot be negative' });
            break;
        }
        case 'standard-error': {
            if (nameHas(col, /std.?err|stderr|(^|_)se(_|$)|standard error/))
                s.push({ points: 0.5, reason: 'name or label says standard error' });
            if (num !== null && num.min >= 0 && num.max < 1)
                s.push({ points: 0.2, reason: 'small non-negative values, as standard errors of proportions are' });
            if (num !== null && num.min < 0)
                caps.push({ cap: 0.05, reason: 'negative values: a standard error cannot be negative' });
            break;
        }
        case 'sample-size': {
            if (nameHas(col, /sample|^n$|respondents|students surveyed|_n$/))
                s.push({ points: 0.45, reason: 'name or label suggests the respondent count' });
            if (num?.integerOnly)
                s.push({ points: 0.15, reason: 'whole-number counts' });
            if (num !== null && num.min >= 0)
                s.push({ points: 0.1, reason: 'non-negative' });
            if (num !== null && !num.integerOnly)
                caps.push({ cap: 0.3, reason: 'fractional values: a sample size is a count' });
            break;
        }
        case 'population-count': {
            if (nameHas(col, /pop|population|enroll|total/))
                s.push({ points: 0.45, reason: 'name or label suggests a population count' });
            if (num?.integerOnly)
                s.push({ points: 0.1, reason: 'whole-number counts' });
            if (num !== null && num.max >= 1000)
                s.push({ points: 0.1, reason: 'large values, as population counts are' });
            break;
        }
        case 'covariate': {
            if (col.type === 'number')
                s.push({ points: 0.35, reason: 'numeric column in an area-level file: a covariate candidate' });
            if (nameHas(col, /pct|percent|rate|poverty|income|enroll|locale|median/))
                s.push({ points: 0.2, reason: 'name or label suggests an area characteristic' });
            if (col.distinctCount < 2)
                caps.push({ cap: 0.1, reason: 'constant across areas: no information (brief 7.2)' });
            break;
        }
        default:
            break;
    }
    return combine(s, caps);
}
export function detectRoles(table, options) {
    const preset = options.preset ?? GENERIC_PRESET;
    const kind = options.kind;
    const baseProfile = options.profile ?? profileTable(table);
    // 'undetermined' files are probed with the microdata role set:
    // it is the richer of the two, and the scores themselves show
    // what the file could support.
    const areaKind = kind === 'area-level' || kind === 'area-covariates';
    const scoreFn = areaKind ? scoreAreaLevel : scoreMicrodata;
    const singletons = areaKind ? AREA_SINGLETONS : MICRO_SINGLETONS;
    const multiRoles = areaKind ? AREA_MULTI : MICRO_MULTI;
    const allRoles = [...singletons, ...multiRoles];
    const ctx = { table, kind, preset };
    const rawScore = (profile, column, role) => {
        const col = profile.columns.find((c) => c.name === column);
        if (col === undefined)
            return { score: 0, reasons: [] };
        return scoreFn(col, role, ctx);
    };
    // Phase 1: provisional area and stratum from base signals.
    const provisionalPick = (profile, role) => {
        let best;
        let bestScore = 0;
        for (const col of profile.columns) {
            const { score } = rawScore(profile, col.name, role);
            if (score > bestScore) {
                bestScore = score;
                best = col.name;
            }
        }
        return bestScore >= REVIEW_THRESHOLD ? best : undefined;
    };
    ctx.provisionalArea = provisionalPick(baseProfile, 'area-id');
    ctx.provisionalStratum =
        !areaKind ? provisionalPick(baseProfile, 'stratum') : undefined;
    // Enriched profile with within-area statistics (DET-01).
    const profile = ctx.provisionalArea !== undefined
        ? profileTable(table, ctx.provisionalArea)
        : baseProfile;
    // Phase 2: full score matrix.
    const matrix = new Map();
    for (const col of profile.columns) {
        const per = new Map();
        for (const role of allRoles)
            per.set(role, rawScore(profile, col.name, role));
        matrix.set(col.name, per);
    }
    const scoreOf = (column, role) => matrix.get(column)?.get(role)?.score ?? 0;
    // Assignment.
    const assignedColumnRole = new Map();
    const contested = [];
    const contestedColumns = new Set();
    for (const role of singletons) {
        const eligible = profile.columns
            .map((c) => c.name)
            .filter((name) => !assignedColumnRole.has(name))
            .map((name) => ({ name, score: scoreOf(name, role) }))
            .sort((a, b) => b.score - a.score);
        const top = eligible[0];
        if (top === undefined || top.score < REVIEW_THRESHOLD)
            continue;
        const runner = eligible[1];
        if (runner !== undefined &&
            runner.score >= REVIEW_THRESHOLD &&
            top.score - runner.score <= CONTEST_WINDOW) {
            contested.push({
                role,
                columns: [top.name, runner.name],
                scores: [top.score, runner.score],
            });
            contestedColumns.add(top.name);
            contestedColumns.add(runner.name);
        }
        assignedColumnRole.set(top.name, { role, score: top.score });
    }
    for (const col of profile.columns) {
        if (assignedColumnRole.has(col.name))
            continue;
        let bestRole = null;
        let bestScore = 0;
        for (const role of multiRoles) {
            const sc = scoreOf(col.name, role);
            if (sc > bestScore) {
                bestScore = sc;
                bestRole = role;
            }
        }
        if (bestRole !== null && bestScore >= REVIEW_THRESHOLD) {
            assignedColumnRole.set(col.name, { role: bestRole, score: bestScore });
        }
    }
    const statusOf = (score) => score >= ACCEPT_THRESHOLD
        ? 'accepted'
        : score >= REVIEW_THRESHOLD
            ? 'review'
            : 'unassigned';
    const decisions = profile.columns.map((col) => {
        const per = matrix.get(col.name);
        const assigned = assignedColumnRole.get(col.name);
        const contenders = allRoles
            .filter((r) => r !== assigned?.role)
            .map((r) => ({ role: r, score: per?.get(r)?.score ?? 0 }))
            .filter((c) => c.score >= REVIEW_THRESHOLD)
            .sort((a, b) => b.score - a.score);
        if (assigned !== undefined) {
            return {
                column: col.name,
                role: assigned.role,
                confidence: assigned.score,
                status: statusOf(assigned.score),
                contested: contestedColumns.has(col.name),
                contenders,
                reasons: per?.get(assigned.role)?.reasons ?? [],
            };
        }
        let bestRole = null;
        let bestScore = 0;
        for (const role of allRoles) {
            const sc = per?.get(role)?.score ?? 0;
            if (sc > bestScore) {
                bestScore = sc;
                bestRole = role;
            }
        }
        return {
            column: col.name,
            role: null,
            confidence: bestScore,
            status: 'unassigned',
            contested: contestedColumns.has(col.name),
            contenders,
            reasons: bestRole !== null ? (per?.get(bestRole)?.reasons ?? []) : [],
        };
    });
    const scores = [];
    for (const col of profile.columns) {
        for (const role of allRoles) {
            const sc = matrix.get(col.name)?.get(role);
            if (sc !== undefined && sc.score >= 0.05) {
                scores.push({ column: col.name, role, score: sc.score, reasons: sc.reasons });
            }
        }
    }
    scores.sort((a, b) => b.score - a.score ||
        profile.columns.findIndex((c) => c.name === a.column) -
            profile.columns.findIndex((c) => c.name === b.column) ||
        a.role.localeCompare(b.role));
    const byRole = (role) => decisions.filter((d) => d.role === role).map((d) => d.column);
    const single = (role) => decisions.find((d) => d.role === role)?.column;
    const roles = {
        ...(single('area-id') !== undefined ? { areaId: single('area-id') } : {}),
        ...(single('area-name') !== undefined ? { areaName: single('area-name') } : {}),
        ...(single('weight') !== undefined ? { weight: single('weight') } : {}),
        ...(single('stratum') !== undefined ? { stratum: single('stratum') } : {}),
        ...(single('psu') !== undefined ? { psu: single('psu') } : {}),
        ...(single('psu-population-count') !== undefined
            ? { psuPopulationCount: single('psu-population-count') }
            : {}),
        ...(single('respondent-id') !== undefined
            ? { respondentId: single('respondent-id') }
            : {}),
        ...(single('estimate') !== undefined ? { estimate: single('estimate') } : {}),
        ...(single('variance') !== undefined ? { variance: single('variance') } : {}),
        ...(single('standard-error') !== undefined
            ? { standardError: single('standard-error') }
            : {}),
        ...(single('sample-size') !== undefined
            ? { sampleSize: single('sample-size') }
            : {}),
        ...(single('population-count') !== undefined
            ? { populationCount: single('population-count') }
            : {}),
        indicators: byRole('indicator'),
        subgroups: byRole('subgroup'),
        covariates: byRole('covariate'),
    };
    return {
        roles: { decisions, scores, roles, contested },
        profile,
    };
}
//# sourceMappingURL=detect.js.map